import React from 'react';
import styled from 'styled-components';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Pencil, RotateCcw, RotateCw } from 'lucide-react';
import type { AppTheme, ToolsConfig } from '../../../core/types';

// Header组件属性接口
export interface HeaderProps {
  // 文件信息
  fileName?: string;
  currentPage?: number;
  totalPage?: number;

  // 工具配置
  tools?: ToolsConfig;

  // 样式配置
  styles?: {
    theme?: AppTheme;
  };

  // 事件回调
  onPrevPage?: () => void;
  onNextPage?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onAnnotationToggle?: () => void;
  onDownload?: () => void;
  onRotateLeft?: () => void;
  onRotateRight?: () => void;
  onClose?: () => void;

  // 状态
  scale?: number;
  isAnnotating?: boolean;

  // 自定义类名
  className?: string;
}

// 样式组件
const HeaderContainer = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  padding: 0px 12px;
  justify-content: space-between;
  padding: ${props => props.$theme.space.padding};
  border-bottom: 1px solid ${props => props.$theme.colors.border};
  background: ${props => props.$theme.colors.background};
  min-height: 52px;
`;

const FileInfo = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div<{ $theme: AppTheme }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PageInfo = styled.div<{ $theme: AppTheme }>`
  font-size: 12px;
  color: ${props => props.$theme.colors.disabledText};
  white-space: nowrap;
`;

const ToolBar = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ToolButton = styled.button<{ $theme: AppTheme; $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${props => props.$theme.space.radius};
  background: ${props => props.$active ? props.$theme.colors.primary + '20' : 'transparent'};
  color: ${props => props.$active ? props.$theme.colors.primary : props.$theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;

  /* 确保图标有默认颜色 */
  & > svg {
    color: currentColor;
    stroke: currentColor;
  }

  &:hover {
    background: ${props => props.$theme.colors.primary + '10'};
    color: ${props => props.$theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ZoomInfo = styled.div<{ $theme: AppTheme }>`
  font-size: 12px;
  color: ${props => props.$theme.colors.disabledText};
  min-width: 40px;
  text-align: center;
`;

// 默认主题
const defaultTheme: AppTheme = {
  colors: {
    primary: '#007bff',
    secondary: '#6c757d',
    success: '#008000',
    warning: '#FFA500',
    error: '#FF0000',
    info: '#0000FF',
    background: '#f8f9fa',
    text: '#343a40',
    border: '#dee2e6',
    disabled: '#808080',
    disabledBackground: '#F5F5F5',
    disabledText: '#808080',
  },
  space: {
    sidebar: '12px',
    size: '12px',
    radius: '4px',
    padding: '8px 12px',
    margin: '4px',
    shadow: '0 2px 4px rgba(0,0,0,0.1)',
    lineHeight: '1.5',
  },
};

/**
 * Header组件
 * 负责文件查看器的头部工具栏
 *
 * @param props - 组件属性
 * @returns Header组件
 */
const Header: React.FC<HeaderProps> = React.memo(({
  fileName = '未知文件',
  currentPage = 1,
  totalPage = 0,
  tools = {
    annotation: true,
    download: true,
    zoom: true,
    close: true,
    navigation: true,
    rotate: true,
  },
  styles,
  onPrevPage,
  onNextPage,
  onZoomIn,
  onZoomOut,
  onAnnotationToggle,
  onDownload,
  onRotateLeft,
  onRotateRight,
  onClose,
  scale = 1,
  isAnnotating = false,
  className,
}) => {
  // 使用主题
  const mergedTheme = styles?.theme || defaultTheme;

  // 添加调试信息
  console.log('Header组件渲染 - currentPage:', currentPage, 'totalPage:', totalPage);

  return (
    <HeaderContainer $theme={mergedTheme} className={className}>
      <FileInfo $theme={mergedTheme}>
        <FileName $theme={mergedTheme}>
          {fileName}
        </FileName>
      </FileInfo>

      <ToolBar $theme={mergedTheme}>
        {/* 页面导航 */}
        {tools.navigation && (
          <>
            <ToolButton
              $theme={mergedTheme}
              onClick={onPrevPage}
              disabled={currentPage <= 1}
              title="上一页"
            >
              <ChevronLeft size={16} />
            </ToolButton>
            {totalPage > 0 && (
              <PageInfo $theme={mergedTheme}>
                {currentPage} / {totalPage}
              </PageInfo>
            )}
            <ToolButton
              $theme={mergedTheme}
              onClick={onNextPage}
              disabled={!totalPage || currentPage >= totalPage}
              title="下一页"
            >
              <ChevronRight size={16} />
            </ToolButton>
          </>
        )}

        {/* 缩放控制 */}
        {tools.zoom && (
          <>
            <ToolButton
              $theme={mergedTheme}
              onClick={onZoomOut}
              disabled={scale <= 0.3}
              title="缩小"
            >
              <ZoomOut size={16} />
            </ToolButton>
            <ZoomInfo $theme={mergedTheme}>
              {Math.round(scale * 100)}%
            </ZoomInfo>
            <ToolButton
              $theme={mergedTheme}
              onClick={onZoomIn}
              disabled={scale >= 3}
              title="放大"
            >
              <ZoomIn size={16} />
            </ToolButton>
          </>
        )}

        {/* 标注工具 */}
        {tools.annotation && (
          <ToolButton
            $theme={mergedTheme}
            $active={isAnnotating}
            onClick={onAnnotationToggle}
            title={isAnnotating ? '退出标注' : '开始标注'}
          >
            <Pencil size={16} />
          </ToolButton>
        )}

        {/* 下载按钮 */}
        {tools.download && (
          <ToolButton
            $theme={mergedTheme}
            onClick={onDownload}
            title="下载"
          >
            <Download size={16} />
          </ToolButton>
        )}

        {/* 旋转控制 */}
        {tools.rotate && (
          <>
            <ToolButton
              $theme={mergedTheme}
              onClick={onRotateLeft}
              title="向左旋转"
            >
              <RotateCcw size={16} />
            </ToolButton>
            <ToolButton
              $theme={mergedTheme}
              onClick={onRotateRight}
              title="向右旋转"
            >
              <RotateCw size={16} />
            </ToolButton>
          </>
        )}

        {/* 关闭按钮 */}
        {tools.close && (
          <ToolButton
            $theme={mergedTheme}
            onClick={onClose}
            title="关闭"
          >
            <X size={16} />
          </ToolButton>
        )}
      </ToolBar>
    </HeaderContainer>
  );
});

export default Header;
